/**
 * Replay and highlights (DESIGN §11). The server folds the command log to any
 * sequence number; we scrub through it, jump to highlights, and look through
 * one table (with the backs) or one player's phone exactly as they saw it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ClockView, Role, SeatView, TableDetail } from '@nightshift/engine/client';
import { linkProps } from '../app/router.ts';
import { api } from '../net/api.ts';
import { PatientCardCompact } from '../player/PatientCard.tsx';
import { Moon, phaseName, Pips, ROLE_TITLE } from '../ui/bits.tsx';
import { FeedRow, type Ctl } from './SessionScreen.tsx';
import { TableDetailView } from './TableView.tsx';
import { playbackTicks, sequenceAt } from './replay-clock.ts';
import type { FeedItem, Timeline } from './wire.ts';

interface Summary {
  id: string;
  name: string;
  code: string;
  seq: number;
  tables: { no: number; seats: Role[] }[];
}
type Frame = { seq: number; clock: ClockView; table?: TableDetail; seat?: SeatView };

/** Keep historical arrival markers and released Action markers distinct. */
export function turnMarks(marks: Timeline['phases']): Timeline['phases'] {
  const arrivals = new Set(marks.filter(mark => mark.phase === 'ARRIVALS').map(mark => mark.turn));
  return marks.filter(mark => mark.phase === 'HANDOVER' || mark.phase === 'ARRIVALS' || (mark.phase === 'ACT' && !arrivals.has(mark.turn)));
}

/** Retain session-scoped state resets before initializing the replay position. */
function useReplayState(sessionId: string) {
  const [sum, setSum] = useState<Summary | null>(null);
  const [tl, setTl] = useState<Timeline | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [seq, setSeq] = useState<number | null>(null);
  const [table, setTable] = useState(1);
  const [seat, setSeat] = useState<Role | null>(null);
  const [frame, setFrame] = useState<Frame | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<1 | 4 | 16>(4);
  const [onlyTable, setOnlyTable] = useState(true);
  const [note, setNote] = useState('');
  const playClock = useRef({ started: 0, elapsed: 0 });
  const inFlight = useRef({ count: 0 });

  const sessionRequest = useRef(0);
  useEffect(() => {
    const id = ++sessionRequest.current;
    inFlight.current = { count: 0 };
    setSum(null); setTl(null); setFrame(null); setSeq(null); setSeat(null); setErr(null); setLoadErr(null); setPlaying(false); setNote('');
    api<Summary>(`/api/sessions/${sessionId}`)
      .then((s) => {
        if (id !== sessionRequest.current) return;
        setSum(s);
        setTable(s.tables[0]?.no ?? 1);
      })
      .catch((e) => {
        console.warn('Session summary unavailable:', sessionId, e);
        if (id === sessionRequest.current) setLoadErr((e as Error).message);
      });
    api<Timeline>(`/api/sessions/${sessionId}/timeline?min=1`)
      .then((t) => { if (id === sessionRequest.current) setTl(t); })
      .catch((e) => {
        console.warn('Saved timeline unavailable:', sessionId, e);
        if (id === sessionRequest.current) setLoadErr((e as Error).message);
      });
    return () => { sessionRequest.current++; };
  }, [sessionId]);

  const first = tl?.phases.find((p) => p.phase !== 'SETUP')?.seq ?? 1;
  const last = sum?.seq ?? 1;
  useEffect(() => {
    if (sum && seq === null) setSeq(last);
  }, [sum, seq, last]);

  return { sum, tl, err, loadErr, seq, setSeq, table, setTable, seat, setSeat, frame, setFrame, loading, setLoading,
    playing, setPlaying, speed, setSpeed, onlyTable, setOnlyTable, note, setNote, setTl, setErr,
    playClock, inFlight, sessionRequest, first, last };
}

type ReplayState = ReturnType<typeof useReplayState>;

/** Preserve stale-frame cancellation and the bounded request lifetime. */
function useReplayFrame(sessionId: string, { seq, table, seat, playing, inFlight, setLoading, setFrame, setErr, setPlaying }: ReplayState): void {
  // Fetch the frame for (seq, table, seat), dropping stale answers.
  const req = useRef(0);
  useEffect(() => {
    if (seq === null) return;
    const id = ++req.current;
    const requests = inFlight.current;
    const controller = new AbortController();
    let deadline: number | undefined;
    setLoading(true);
    if (!playing) setFrame(null);
    const t = window.setTimeout(() => {
      requests.count++;
      deadline = window.setTimeout(() => controller.abort(new DOMException('Replay request timed out.', 'TimeoutError')), 30_000);
      api<Frame>(`/api/sessions/${sessionId}/replay?seq=${seq}&table=${table}${seat ? `&seat=${seat}` : ''}`, { signal: controller.signal })
        .then((f) => {
          if (id === req.current) {
            setFrame(f);
            setErr(null);
          }
        })
        .catch((e) => {
          console.warn('Replay unavailable:', sessionId, seq, e);
          if (id === req.current) { setFrame(null); setErr((e as Error).message); setPlaying(false); }
        })
        .finally(() => {
          window.clearTimeout(deadline);
          requests.count--;
          if (id === req.current) setLoading(false);
        });
    }, 120);
    return () => { window.clearTimeout(t); window.clearTimeout(deadline); req.current++; controller.abort(); };
  }, [sessionId, seq, table, seat]);

}

/** Keep the playback clock and its controls on the original render sequence. */
function useReplayPlayback({ tl, first, speed, playing, seq, loading, inFlight, playClock, setSeq, setPlaying }: ReplayState) {
  const marks = useMemo(() => (tl?.phases ?? []).filter((p) => p.phase !== 'SETUP'), [tl]);
  const ticks = useMemo(() => playbackTicks((tl?.ticks ?? []).filter(([s]) => s >= first), speed), [tl, first, speed]);
  useEffect(() => {
    if (!playing || seq === null || !ticks.length) return;
    const t = window.setInterval(() => {
      if (loading || inFlight.current.count) return;
      const elapsed = playClock.current.elapsed + performance.now() - playClock.current.started;
      const next = sequenceAt(ticks, elapsed);
      if (next !== seq) setSeq(next);
      else if (elapsed >= ticks[ticks.length - 1][1]) setPlaying(false);
    }, 400);
    return () => window.clearInterval(t);
  }, [playing, seq, loading, ticks]);

  function seek(next: number): void {
    setPlaying(false);
    setSeq(next);
  }

  function togglePlay(): void {
    if (playing) { setPlaying(false); return; }
    if (loading || inFlight.current.count) return;
    const start = seq !== null && seq < ticks[ticks.length - 1][0] ? seq : ticks[0][0];
    const elapsed = ticks.findLast(([s]) => s <= start)?.[1] ?? 0;
    playClock.current = { started: performance.now(), elapsed };
    setSeq(start);
    setPlaying(true);
  }

  return { marks, ticks, seek, togglePlay };
}

export function Replay({ sessionId }: { sessionId: string }) {
  const state = useReplayState(sessionId);
  const { sum, tl, err, loadErr, seq, table, setTable, seat, setSeat, frame, loading, playing, setPlaying, speed, setSpeed,
    onlyTable, setOnlyTable, note, setNote, setTl, setErr, sessionRequest, first, last } = state;
  useReplayFrame(sessionId, state);
  const { marks, ticks, seek, togglePlay } = useReplayPlayback(state);

  const highlights: FeedItem[] = useMemo(() => (tl?.events ?? []).filter((e) => e.severity >= 2 && (!onlyTable || e.table === table || e.table === null)), [tl, onlyTable, table]);
  const ctl: Ctl = { send: async () => false, readonly: true, sessionId };
  const noteTable = frame?.table?.no ?? frame?.seat?.me.table;

  if (loadErr) return <ReplayUnavailable sessionId={sessionId} loadErr={loadErr} tl={tl} />;
  if (!sum || !tl || seq === null) return <main className="fac-loading muted">Loading the log…</main>;
  const seatsHere = sum.tables.find((t) => t.no === table)?.seats ?? [];

  return (
    <div className="fac-page replay">
      <ReplayHeader sessionId={sessionId} sum={sum} frame={frame} loading={loading} />

      <ReplayScrub playing={playing} togglePlay={togglePlay} ticks={ticks} loading={loading} speed={speed} setPlaying={setPlaying} setSpeed={setSpeed}
        seek={seek} marks={marks} seq={seq} first={first} last={last} frame={frame} highlights={highlights} />

      <div className="session-body">
        <main className="session-main">
          <div className="row wrap replay-pick">
            {sum.tables.map((t) => (
              <button key={t.no} className="chip" aria-pressed={table === t.no} onClick={() => { setPlaying(false); setTable(t.no); }}>
                Table {t.no}
              </button>
            ))}
            <span className="sep" />
            <button className="chip" aria-pressed={seat === null} onClick={() => { setPlaying(false); setSeat(null); }}>
              Whole table
            </button>
            {seatsHere.map((r) => (
              <button key={r} className={`chip spec-${r}`} aria-pressed={seat === r} onClick={() => { setPlaying(false); setSeat(r); }}>
                <span className="spec-dot" /> {ROLE_TITLE[r]}’s phone
              </button>
            ))}
          </div>
          {err && <p className="form-error">{err}</p>}
          {frame?.table && !seat && <TableDetailView d={frame.table} spoilers ctl={ctl} feed={[]} backsDefault />}
          {frame?.seat && seat && <SeatEye v={frame.seat} />}
        </main>
        <aside className="rail-side">
          <ReplayNote note={note} frame={frame} noteTable={noteTable} sessionRequest={sessionRequest} sessionId={sessionId}
            setNote={setNote} setTl={setTl} setErr={setErr} />
          {tl.notes.length > 0 && (
            <ReplayNotes tl={tl} seek={seek} />
          )}
          <ReplayHighlights onlyTable={onlyTable} setOnlyTable={setOnlyTable} table={table} highlights={highlights} seek={seek} />
        </aside>
      </div>
    </div>
  );
}

interface ReplayNoteProps {
  note: string; frame: Frame | null; noteTable: number | undefined;
  sessionRequest: { current: number }; sessionId: string;
  setNote: (note: string) => void; setTl: (timeline: Timeline) => void; setErr: (error: string) => void;
}

/** Preserve note writes and refresh guards for the selected session. */
function ReplayNote({ note, frame, noteTable, sessionRequest, sessionId, setNote, setTl, setErr }: ReplayNoteProps) {
  return (
    <form
            className="note-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!note.trim() || !frame || noteTable === undefined) return;
              const id = sessionRequest.current;
              try {
                await api(`/api/sessions/${sessionId}/notes`, { body: { table: noteTable, seq: frame.seq, seat: frame.seat?.me.role ?? null, text: note.trim() } });
                const timeline = await api<Timeline>(`/api/sessions/${sessionId}/timeline?min=1`);
                if (id === sessionRequest.current) { setNote(''); setTl(timeline); }
              } catch (e) {
                console.warn('Note save or refresh failed:', sessionId, e);
                if (id === sessionRequest.current) setErr((e as Error).message);
              }
            }}
          >
            <label className="sec-title" htmlFor="rnote">
              Note at this moment
            </label>
            <textarea id="rnote" className="input" rows={2} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
            <button className="btn btn-small" disabled={!note.trim() || noteTable === undefined}>
              Save note
            </button>
          </form>
  );
}

/** Keep saved notes linked to their recorded sequence. */
function ReplayNotes({ tl, seek }: { tl: Pick<Timeline, 'notes'>; seek: (seq: number) => void }) {
  return (
    <section>
              <h3 className="sec-title">Notes</h3>
              <ol className="feed-list">
                {tl.notes.map((n) => (
                  <li key={n.id} className="fr">
                    <button className="fr-btn" onClick={() => n.seq && seek(n.seq)} disabled={!n.seq}>
                      <span className="fr-when mono">{n.starred ? '★' : ''}</span>
                      <span className="fr-text">
                        {n.table_no ? `Table ${n.table_no}: ` : ''}
                        {n.text}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </section>
  );
}

/** Filter highlighted events using the existing table selection. */
function ReplayHighlights({ onlyTable, setOnlyTable, table, highlights, seek }: {
  onlyTable: boolean; setOnlyTable: (onlyTable: boolean) => void; table: number; highlights: FeedItem[]; seek: (seq: number) => void;
}) {
  return (
    <section className="feed">
            <div className="row feed-head">
              <h3 className="sec-title">Highlights</h3>
              <label className="row small">
                <input type="checkbox" checked={onlyTable} onChange={(e) => setOnlyTable(e.target.checked)} /> Table {table} only
              </label>
            </div>
            <ol className="feed-list">
              {highlights.map((f, i) => (
                <FeedRow key={`${f.seq}-${i}`} f={f} onClick={() => seek(f.seq)} />
              ))}
              {highlights.length === 0 && <li className="muted">No highlights.</li>}
            </ol>
          </section>
  );
}

/** Keep archived timeline access available when a replay cannot load. */
function ReplayUnavailable({ sessionId, loadErr, tl }: { sessionId: string; loadErr: string; tl: Timeline | null }) {
  return (
    <main className="fac-page replay">
    <a {...linkProps('/control')}>Back to sessions</a>
    <h1>Saved timeline</h1>
    <p role="alert">{loadErr}</p>
    {tl && <>
      <a className="btn btn-small" href={`/api/sessions/${sessionId}/export.csv`} download>Export CSV</a>
      <h2>Notes</h2>
      <ul>{tl.notes.map((n) => <li key={n.id}>{n.text}</li>)}</ul>
      <h2>Events</h2>
      <ol className="feed-list">{tl.events.map((f, i) => <FeedRow key={`${f.seq}-${i}`} f={f} />)}</ol>
    </>}
  </main>
  );
}

/** Display the selected frame's clock and export link. */
function ReplayHeader({ sessionId, sum, frame, loading }: { sessionId: string; sum: Summary; frame: Frame | null; loading: boolean }) {
  return (
    <header className="clockbar">
        <div className="cb-left">
          <a className="cb-back" {...linkProps(`/control/s/${sessionId}`)} aria-label="Back to the session">
            <Moon size={22} />
          </a>
          <div className="cb-title">
            <strong>Replay: {sum.name}</strong>
            <span className="muted">Move through the night, open any table or any phone.</span>
          </div>
        </div>
        {frame && (
          <div className="cb-clock">
            <span className="cb-hour display">{frame.clock.hour}</span>
            <span className="cb-phase">
              <strong>{phaseName(frame.clock.phase)}</strong>
              <span className="muted">
                Turn {frame.clock.turn} of {frame.clock.lastTurn}
              </span>
            </span>
            {loading && <span className="muted small">loading</span>}
          </div>
        )}
        <div className="cb-controls">
          <a className="btn btn-small btn-quiet" href={`/api/sessions/${sessionId}/export.json`} download>
            Export log
          </a>
        </div>
      </header>
  );
}

interface ReplayScrubProps {
  playing: boolean; loading: boolean; speed: 1 | 4 | 16;
  seq: number; first: number; last: number; frame: Frame | null;
  ticks: [number, number][]; marks: Timeline['phases']; highlights: FeedItem[];
  togglePlay: () => void; seek: (seq: number) => void;
  setPlaying: (playing: boolean) => void; setSpeed: (speed: 1 | 4 | 16) => void;
}

/** Keep playback controls and timeline markers together. */
function ReplayScrub({ playing, togglePlay, ticks, loading, speed, setPlaying, setSpeed, seek, marks, seq, first, last, frame, highlights }: ReplayScrubProps) {
  const pct = (s: number) => ((s - first) / Math.max(1, last - first)) * 100;
  return (
    <section className="scrub" aria-label="Timeline">
        <div className="row wrap">
          <button className="btn btn-small" onClick={togglePlay} disabled={!ticks.length || (!playing && loading)} data-testid="replay-play">
            {playing ? 'Stop' : 'Play'}
          </button>
          <label className="row small">
            Playback speed
            <select className="input" value={speed} onChange={(e) => { setPlaying(false); setSpeed(Number(e.target.value) as 1 | 4 | 16); }}>
              {[1, 4, 16].map((s) => <option key={s} value={s}>{s}×</option>)}
            </select>
          </label>
          <button className="btn btn-small btn-quiet" onClick={() => seek(Math.max(first, [...marks].reverse().find((m) => m.seq < seq)?.seq ?? first))}>
            Back a phase
          </button>
          <button className="btn btn-small btn-quiet" onClick={() => seek(marks.find((m) => m.seq > seq)?.seq ?? last)}>
            Forward a phase
          </button>
          <span className="muted small mono" data-testid="replay-time">
            {playing && frame ? `${frame.clock.hour}, turn ${frame.clock.turn}, ${phaseName(frame.clock.phase)}` : `step ${seq}`}
          </span>
        </div>
        <div className="scrub-track">
          <div className="scrub-bands" aria-label="Phases">
            {marks.map((m, i) => <span className="scrub-band" key={m.seq} title={`Turn ${m.turn}: ${phaseName(m.phase)}`}
              style={{ left: `${pct(m.seq)}%`, width: `${pct(marks[i + 1]?.seq ?? last) - pct(m.seq)}%` }}>{phaseName(m.phase)}</span>)}
          </div>
          <input type="range" min={first} max={last} value={seq} onChange={(e) => seek(Number(e.target.value))} aria-label="Moment in the shift" data-testid="replay-scrub" />
          <div className="scrub-marks" aria-hidden>
            {turnMarks(marks)
              .map((m) => (
                <span key={m.seq} className="mark" style={{ left: `${pct(m.seq)}%` }}>
                  {m.phase === 'HANDOVER' ? '0700' : `T${m.turn}`}
                </span>
              ))}
            {highlights
              .filter((h) => h.severity >= 3)
              .map((h, i) => (
                <i key={`${h.seq}-${i}`} className="hl" style={{ left: `${pct(h.seq)}%` }} />
              ))}
          </div>
        </div>
      </section>
  );
}

/** Render the same clock, role and workload shown on the recorded phone. */
function SeatEyeHeader({ v }: { v: SeatView }) {
  return (
    <header className={`shiftbar spec-${v.me.role}`}>
          <div className="sb-row">
            <span className="sb-hour display">{v.clock.hour}</span>
            <span className="sb-turn">
              Turn {v.clock.turn} of {v.clock.lastTurn}
              <span className="sb-phase">{phaseName(v.clock.phase)}</span>
            </span>
          </div>
          <div className="sb-row sb-me">
            <span className="sb-role">
              <span className="spec-dot" />
              {v.me.title}
              {v.me.isLead && <span className="lead-badge">Lead</span>}
            </span>
            <span className="sb-stats">
              <Pips available={v.me.actions.available} spent={v.me.actions.spent} />
              <span>{v.workload.census} patients / {v.workload.beds} beds{v.workload.bedExcess > 0 ? ` · ${v.workload.bedExcess} without a bed` : ''}</span>
            </span>
          </div>
        </header>
  );
}

/** One player's phone at that moment: their list exactly as projected to them. */
export function SeatEye({ v }: { v: SeatView }) {
  const patients = [...v.beds.filter((b) => b.patient).map((b) => ({ p: b.patient!, here: b.label })), ...v.corridor.map((p) => ({ p, here: 'Corridor' }))];
  const could = [
    ...v.seatActions.filter((a) => a.enabled).map((a) => ({ key: a.id, who: null as string | null, label: a.label })),
    ...patients.flatMap(({ p }) => p.actions.filter((a) => a.enabled).map((a) => ({ key: `${p.id}-${a.id}`, who: p.name as string | null, label: a.label }))).slice(0, 30),
  ];
  return (
    <div className="seat-eye">
      <div className="phone">
        <SeatEyeHeader v={v} />
        <div className="phone-body list">
          {v.me.huddleSentence && <p className="phase-note">“{v.me.huddleSentence}”</p>}
          {patients.length === 0 && <p className="empty">Nobody on the list.</p>}
          {patients.map(({ p, here }) => (
            <PatientCardCompact key={p.id} p={p} here={here} onOpen={() => {}} />
          ))}
          {v.opened.length > 0 && <h3 className="sec-title">Opened to them</h3>}
          {v.opened.map((p) => (
            <PatientCardCompact key={p.id} p={p} onOpen={() => {}} />
          ))}
        </div>
      </div>
      <aside className="seat-eye-side">
        <h3 className="sec-title">What they could do</h3>
        {could.length === 0 ? (
          <p className="muted small" data-testid="could-none">
            Nothing at this moment: {['ACT', 'HANDOVER'].includes(v.clock.phase) ? 'no actions left' : `actions open in the Action phase, not in ${v.clock.phase === 'DEBRIEF' || v.clock.phase === 'ENDED' ? 'the debrief' : 'this phase'}`}.
          </p>
        ) : (
          <ul className="plain small">
            {could.map((c) => (
              <li key={c.key}>
                {c.who && <span className="muted">{c.who}:</span>} {c.label}
              </li>
            ))}
          </ul>
        )}
        {v.me.objective && (
          <article className="objective">
            <span className="muted">Their objective</span>
            <h3>{v.me.objective.title}</h3>
            <p className="small">{v.me.objective.text}</p>
          </article>
        )}
      </aside>
    </div>
  );
}
